<template>
  <div class="max-w-full mx-auto p-5 font-sans">
    <el-dialog v-model="newCustomerDialogVisible"
               :title="t('addCustomer')">
      <NewCustomerDialog @success="onCustomerCreated"></NewCustomerDialog>
    </el-dialog>

    <!-- Top Header with Inline Form -->
    <div class="flex gap-3 mb-6">
      <div class="flex-1">
        <el-select
            clearable
            class="w-full"
            value-key="contactDisplayName"
            v-model="selectedCustomer"
            filterable
            remote
            reserve-keyword
            :placeholder="t('searchCustomer')"
            :remote-method="remoteSearch"
            :loading="loading"
        >
          <el-option
              v-for="option in customerOptions"
              :key="option.id"
              :label="option.name"
              :value="option"
          ></el-option>
        </el-select>
      </div>
      <button type="button"
          @click="() => { newCustomerDialogVisible = true }"
          class="px-4 bg-gray-200 text-black rounded-md hover:bg-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400"
      >
        {{ t('addCustomer') }}
      </button>
      <button type="button"
          @click="() => { $router.push($Route.CUSTOMERS_GLASSES_JOBS_MONITOR) }"
          class="rounded-xl border border-input bg-input p-1 text-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring focus:ring-offset-0"
      >
        <Icon icon="line-md:monitor-screenshot-twotone" width="24" height="24"/>
      </button>
    </div>

    <div class="flex flex-col md:flex-row gap-6">
      <!-- Waiting Column -->
      <div class="flex-1 bg-amber-50 rounded-lg p-4 shadow-sm border-2 border-amber-200">
        <div class="flex items-center justify-between mb-1 text-gray-700 text-xl font-semibold">
          <div class="flex items-center gap-2">
            <Icon icon="mdi:clock-outline" width="24" height="24" class="text-amber-600"/>
            <h2>{{ t('waiting') }}</h2>
          </div>
          <span>{{ waitingJobs.length }}</span>
        </div>
        <draggable
            v-model="waitingJobs"
            group="jobs"
            item-key="id"
            class="min-h-[400px]"
            @end="saveJobs"
        >
          <template #item="{ element }">
            <JobCard
                :job="element"
                :is-editing="editingJob?.id === element.id"
                :editing-notes="editingNotes"
                @update:editing-notes="editingNotes = $event"
                @move-to-progress="moveToInProgress(element)"
                @move-to-ready="moveToReady(element)"
                @edit-notes="startEditing(element)"
                @save-notes="saveNotes(element)"
                @delete="deleteJob(element)"
            />
          </template>
        </draggable>
      </div>

      <!-- In Progress Column -->
      <div class="flex-1 bg-blue-50 rounded-lg p-4 shadow-sm border-2 border-blue-200">
        <div class="flex items-center justify-between mb-1 text-gray-700 text-xl font-semibold">
          <div class="flex items-center gap-2">
            <Icon icon="mdi:progress-wrench" width="24" height="24" class="text-blue-600"/>
            <h2>{{ t('inProgress') }}</h2>
          </div>
          <span>{{ inProgressJobs.length }}</span>
        </div>
        <draggable
            v-model="inProgressJobs"
            group="jobs"
            item-key="id"
            class="min-h-[400px]"
            @end="saveJobs"
        >
          <template #item="{ element }">
            <JobCard
                :job="element"
                :is-editing="editingJob?.id === element.id"
                :editing-notes="editingNotes"
                @update:editing-notes="editingNotes = $event"
                @move-to-waiting="moveToWaiting(element)"
                @move-to-ready="moveToReady(element)"
                @edit-notes="startEditing(element)"
                @save-notes="saveNotes(element)"
                @delete="deleteJob(element)"
            />
          </template>
        </draggable>
      </div>

      <!-- Ready Column -->
      <div class="flex-1 bg-green-50 rounded-lg p-4 shadow-sm border-2 border-green-200">
        <div class="flex items-center justify-between mb-1 text-gray-700 text-xl font-semibold">
          <div class="flex items-center gap-2">
            <Icon icon="mdi:check-circle-outline" width="24" height="24" class="text-green-600"/>
            <h2>{{ t('ready') }}</h2>
          </div>
          <span>{{ readyJobs.length }}</span>
        </div>
        <draggable
            v-model="readyJobs"
            group="jobs"
            item-key="id"
            class="min-h-[400px]"
            @end="saveJobs"
        >
          <template #item="{ element }">
            <JobCard
                :job="element"
                :is-editing="editingJob?.id === element.id"
                :editing-notes="editingNotes"
                @update:editing-notes="editingNotes = $event"
                @move-to-waiting="moveToWaiting(element)"
                @move-to-progress="moveToInProgress(element)"
                @edit-notes="startEditing(element)"
                @save-notes="saveNotes(element)"
                @delete="deleteJob(element)"
            />
          </template>
        </draggable>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, computed, watch } from 'vue'
import draggable from 'vuedraggable'
import { useI18n } from 'vue-i18n'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Icon } from '@iconify/vue'
import { useHTTP } from '@/plugins/http'
import GlassesJob from '@/models/customer/GlassesJob'
import Customer from '@/models/customer/Customer'
import NewCustomerDialog from '@/dialogs/customer/NewCustomerDialog.vue'
import JobCard from '@/views/customers/components/GlassesJobCard.vue'

const { t } = useI18n({
  messages: {
    en: {
      glassesJobs: 'Glasses Jobs',
      addToList: 'Add to list',
      waiting: 'Waiting',
      inProgress: 'In Progress',
      ready: 'Ready',
      customer: 'Customer',
      notes: 'Notes',
      searchCustomer: 'Search for customer',
      addCustomer: 'Add Customer',
      cancel: 'Cancel',
      add: 'Add',
      save: 'Save',
      editNotes: 'Edit Notes',
      deleteConfirmTitle: 'Delete Glasses Job',
      deleteConfirmMessage: 'Are you sure you want to delete this glasses job?',
      confirm: 'Confirm',
      jobDeleted: 'Glasses job deleted successfully',
      jobAdded: 'Glasses job added successfully',
      jobUpdated: 'Glasses job updated successfully',
      errorLoadingJobs: 'Error loading glasses jobs',
      errorAddingJob: 'Error adding glasses job',
      errorUpdatingJob: 'Error updating glasses job',
      errorDeletingJob: 'Error deleting glasses job'
    },
    ar: {
      glassesJobs: 'طلبات النظارات',
      addToList: 'إضافة الى القائمة',
      waiting: 'في الانتظار',
      inProgress: 'قيد الإنجاز',
      ready: 'جاهزة',
      customer: 'العميل',
      notes: 'ملاحظات',
      searchCustomer: 'البحث عن عميل',
      addCustomer: 'إضافة عميل',
      cancel: 'إلغاء',
      add: 'إضافة',
      save: 'حفظ',
      editNotes: 'تعديل الملاحظات',
      deleteConfirmTitle: 'حذف طلب النظارة',
      deleteConfirmMessage: 'هل أنت متأكد من حذف هذا الطلب؟',
      confirm: 'تأكيد',
      jobDeleted: 'تم حذف طلب النظارة بنجاح',
      jobAdded: 'تمت إضافة طلب النظارة بنجاح',
      jobUpdated: 'تم تحديث طلب النظارة بنجاح',
      errorLoadingJobs: 'خطأ في تحميل طلبات النظارات',
      errorAddingJob: 'خطأ في إضافة طلب النظارة',
      errorUpdatingJob: 'خطأ في تحديث طلب النظارة',
      errorDeletingJob: 'خطأ في حذف طلب النظارة'
    }
  }
})

const http = useHTTP()
const allJobs = ref<GlassesJob[]>([])
const loading = ref(false)
const newCustomerDialogVisible = ref(false)
const customerOptions = ref<Customer[]>([])
const selectedCustomer = ref<Customer | null>(null)
const editingJob = ref<GlassesJob | null>(null)
const editingNotes = ref('')

const waitingJobs = computed({
  get: () => allJobs.value.filter(j => j.status === 'waiting').sort((a, b) => a.position - b.position),
  set: (val) => {
    val.forEach((job, index) => {
      job.status = 'waiting'
      job.position = index
    })
  }
})

const inProgressJobs = computed({
  get: () => allJobs.value.filter(j => j.status === 'in_progress').sort((a, b) => a.position - b.position),
  set: (val) => {
    val.forEach((job, index) => {
      job.status = 'in_progress'
      job.position = index
    })
  }
})

const readyJobs = computed({
  get: () => allJobs.value.filter(j => j.status === 'ready').sort((a, b) => a.position - b.position),
  set: (val) => {
    val.forEach((job, index) => {
      job.status = 'ready'
      job.position = index
    })
  }
})

async function loadJobs() {
  try {
    const response = await http.get<any[]>(GlassesJob.ENDPOINT)
    allJobs.value = response.map(job => GlassesJob.from(job))
  } catch (error) {
    console.error('Error loading glasses jobs:', error)
    ElMessage.error(t('errorLoadingJobs'))
  }
}

async function addJob() {
  if (!selectedCustomer.value) return

  try {
    const payload = {
      customerID: selectedCustomer.value.id,
      status: 'waiting',
      notes: '',
      position: waitingJobs.value.length
    }

    const response = await http.post<any>(GlassesJob.ENDPOINT, payload)
    allJobs.value.push(GlassesJob.from(response))

    ElMessage.success(t('jobAdded'))
    // Reset form
    selectedCustomer.value = null
  } catch (error) {
    console.error('Error adding glasses job:', error)
    ElMessage.error(t('errorAddingJob'))
  }
}

async function saveJobs() {
  try {
    const updates = [...waitingJobs.value, ...inProgressJobs.value, ...readyJobs.value]

    await Promise.all(
      updates.map(job =>
        http.put(`${GlassesJob.ENDPOINT}/${job.id}`, {
          status: job.status,
          notes: job.notes,
          position: job.position
        })
      )
    )
  } catch (error) {
    console.error('Error saving jobs:', error)
    ElMessage.error(t('errorUpdatingJob'))
  }
}

async function updateJobStatus(job: GlassesJob, newStatus: 'waiting' | 'in_progress' | 'ready') {
  try {
    const statusJobs = allJobs.value.filter(j => j.status === newStatus)
    job.status = newStatus
    job.position = statusJobs.length

    await http.put(`${GlassesJob.ENDPOINT}/${job.id}`, {
      status: job.status,
      notes: job.notes,
      position: job.position
    })

    await loadJobs()
  } catch (error) {
    console.error('Error updating job:', error)
    ElMessage.error(t('errorUpdatingJob'))
  }
}

function moveToWaiting(job: GlassesJob) {
  updateJobStatus(job, 'waiting')
}

function moveToInProgress(job: GlassesJob) {
  updateJobStatus(job, 'in_progress')
}

function moveToReady(job: GlassesJob) {
  updateJobStatus(job, 'ready')
}

function startEditing(job: GlassesJob) {
  editingJob.value = { ...job }
  editingNotes.value = job.notes || ''
}

async function saveNotes(job: GlassesJob) {
  if (!editingJob.value) return

  try {
    // First update notes on the backend
    await http.put(`${GlassesJob.ENDPOINT}/${job.id}`, {
      status: job.status,
      notes: editingNotes.value,
      position: job.position
    })

    // Only update frontend state after successful backend update
    job.notes = editingNotes.value

    // Clear editing state
    editingJob.value = null
    editingNotes.value = ''
    ElMessage.success(t('jobUpdated'))
  } catch (error) {
    console.error('Error saving notes:', error)
    ElMessage.error(t('errorUpdatingJob'))
    // Don't update frontend state if backend update fails
  }
}

async function deleteJob(job: GlassesJob) {
  try {
    await ElMessageBox.confirm(
      t('deleteConfirmMessage'),
      t('deleteConfirmTitle'),
      {
        confirmButtonText: t('confirm'),
        cancelButtonText: t('cancel'),
        type: 'warning',
      }
    )

    await http.delete(`${GlassesJob.ENDPOINT}/${job.id}`)
    allJobs.value = allJobs.value.filter(j => j.id !== job.id)
    ElMessage.success(t('jobDeleted'))
  } catch (error) {
    if (error !== 'cancel') {
      console.error('Error deleting job:', error)
      ElMessage.error(t('errorDeletingJob'))
    }
  }
}

async function remoteSearch(query: string) {
  if (!query) query = ''

  try {
    loading.value = true
    const url = `${Customer.ENDPOINT}?q=${query}`
    const response = await http.get<Customer[]>(url)
    customerOptions.value = response.map(c => Customer.from(c))
  } catch (error) {
    console.error('Error searching customers:', error)
    customerOptions.value = []
  } finally {
    loading.value = false
  }
}

async function onCustomerCreated(customer: Customer) {
  customerOptions.value = [...customerOptions.value, customer]
  newCustomerDialogVisible.value = false

  // Directly add the new customer to the list
  try {
    const payload = {
      customerID: customer.id,
      status: 'waiting',
      notes: '',
      position: waitingJobs.value.length
    }

    const response = await http.post<any>(GlassesJob.ENDPOINT, payload)
    allJobs.value.push(GlassesJob.from(response))
    ElMessage.success(t('jobAdded'))
  } catch (error) {
    console.error('Error adding glasses job:', error)
    ElMessage.error(t('errorAddingJob'))
  }
}

// Watch for customer selection and automatically add to list
watch(selectedCustomer, (newCustomer) => {
  if (newCustomer) {
    addJob()
  }
})

onMounted(() => {
  loadJobs()
})
</script>

<style scoped>
/* Ensure proper scrolling in columns */
.overflow-y-auto {
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(155, 155, 155, 0.5) transparent;
}

.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: rgba(155, 155, 155, 0.5);
  border-radius: 20px;
}
</style>
